<template>
  <b-modal
    ref="modal"
    v-model="isModalOpened"
    data-layout-folder-browser-modal
    size="xl"
    centered
    scrollable
    title="Folders"
    ok-title="Accept"
    cancel-variant="outline-primary"
    :ok-disabled="isConfirmDisabled"
    @ok="confirm"
    @hidden="handleModalClose"
  >
    <div class="d-flex actions pl-2 justify-content-between align-items-center">
      <div class="d-flex">
        <div
          class="action-btn"
          :class="{ active: type === 'Move' }"
          @click="setType('Move')"
        >
          <span>Move to folder</span>
        </div>
        <div
          class="action-btn"
          :class="{ active: type === 'Copy' }"
          @click="setType('Copy')"
        >
          <span>Copy to folder</span>
        </div>
      </div>
      <div class="ml-auto d-flex align-items-center">
        <a
          v-if="hasActiveSearch"
          href="#"
          class="clear-search-btn mr-3"
          @click="clearSearch"
        >
          <i class="bi bi-x-circle mr-1" />
          Clear search
        </a>
        <a
          v-if="lastUsedFolder"
          href="#"
          class="use-last-folder-btn"
          @click="useLastFolder"
        >
          Use last folder ({{ lastUsedFolder.name }})
        </a>
      </div>
    </div>

    <layout-folder-browser :is-modal-instance="true" />
  </b-modal>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import {bus} from "@frontend/app";
import Requests from "@frontend/services/api/layout-folder";

const { mapActions, mapGetters } = createNamespacedHelpers('layoutFolderBrowser')
export default {
  name: "LayoutFolderBrowserModal",
  props: {
    companyId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isModalOpened: true,
      type: 'Move',
      lastUsedFolder: null,
    }
  },
  computed: {
    ...mapGetters(['isFileBrowserModalOpened', 'selectedFolders', 'selectedCount', 'fileBrowserModalType', 'selectedItems', 'items', 'searchQuery']),
    ...createNamespacedHelpers('gridViewMemory').mapGetters(['excludedIds']),

    isConfirmDisabled() {
      let isDisabled = true;

      if (this.selectedFolders.length > 0 && this.selectedFolders.length === this.selectedCount && this.type) {
        isDisabled = false;
      }

      return isDisabled;
    },

    hasActiveSearch() {
      return this.searchQuery && this.searchQuery.length > 0;
    }
  },
  watch: {
    isFileBrowserModalOpened: {
      immediate: true,
      handler(newValue) {
        this.isModalOpened = newValue;
      },
    },
    isModalOpened(newValue) {
      this.setIsFileBrowserModalOpened(newValue);
      if (newValue) {
        if (this.fileBrowserModalType) {
          this.setType(this.fileBrowserModalType);
        } else {
          this.setType('Move');
        }
      }
    },
    fileBrowserModalType: {
      immediate: true,
      handler(type) {
        if (type === 'Copy' || type === 'Move' || type === 'Link') {
          this.setType(type)
          this.setActionType(type);
        }
      }
    }
  },
  mounted() {
    this.loadLastUsedFolder();
  },
  methods: {
    ...mapActions([
      'setIsFileBrowserModalOpened', 
      'disableMultiselect', 
      'setActionType', 
      'clearSelection', 
      'clearSelections'
    ]),
    confirm() {
      if (this.selectedItems.length > 0) {
        this.saveLastUsedFolder(this.selectedItems[0]);
      }

      bus.$emit('folderActionConfirmed', {
        type: this.type,
        items: this.selectedItems.map(item => ({ id: item.id, type: 'layout', name: item.name })),
      });

      this.$store.commit('gridViewMemory/RESET_MEMORY')
      this.setIsFileBrowserModalOpened(false);
    },
    handleModalClose() {
      this.$store.commit('gridViewMemory/RESET_MEMORY')
      this.disableMultiselect();
      this.setActionType(null);
      this.clearSelections();
    },
    setType(newType) {
      if (this.type === newType) {
        this.setActionType(newType);
        return;
      }

      this.clearSelection();
      this.type = newType;
      this.setActionType(newType);
    },

    getLastUsedFolderKey() {
      return `last-used-folder-${this.companyId}`;
    },

    loadLastUsedFolder() {
      try {
        const key = this.getLastUsedFolderKey();
        const stored = localStorage.getItem(key);

        if (stored) {
          this.lastUsedFolder = JSON.parse(stored);
        }
      } catch (e) {
        console.error('Failed to load last used folder:', e);
      }
    },

    saveLastUsedFolder(folder) {
      try {
        const key = this.getLastUsedFolderKey();

        const folderData = {
          id: folder.id,
          name: folder.name,
          parent_id: folder.parent_id
        };

        localStorage.setItem(key, JSON.stringify(folderData));

        this.lastUsedFolder = folderData;
      } catch (e) {
        console.error('Failed to save last used folder:', e);
      }
    },

    clearSearch(e) {
      if (e) {
        e.preventDefault();
      }

      this.$store.dispatch('layoutFolderBrowser/setSearchQuery', '');
      this.$store.dispatch('layoutFolderBrowser/setCurrentFolder', {
        id: null,
        name: null
      });
    },

    async useLastFolder(e) {
      if (e) {
        e.preventDefault();
      }

      if (!this.lastUsedFolder) {
        return;
      }

      const folderInItems = this.items.find(item => item.id === this.lastUsedFolder.id);

      if (folderInItems) {
        this.$store.commit('layoutFolderBrowser/SET_SELECTION', [this.lastUsedFolder.id]);
      } else {
        try {
          await this.$store.dispatch('layoutFolderBrowser/setCurrentFolder', {
            id: this.lastUsedFolder.parent_id || null,
            name: null
          });

          const { data } = await Requests.getFolders({
            pageSize: 100,
            pageNumber: 1,
            searchKey: this.lastUsedFolder.name,
            parentId: this.lastUsedFolder.parent_id || null,
            sortBy: 'created_at',
            sortDirection: 'asc'
          });

          const foundFolder = data.items.find(item => item.id === this.lastUsedFolder.id);

          if (foundFolder) {
            this.$store.commit('layoutFolderBrowser/SET_ITEMS', data.items);
            this.$store.commit('layoutFolderBrowser/SET_SEARCH_QUERY', this.lastUsedFolder.name);

            this.$store.commit('layoutFolderBrowser/SET_SELECTION', [this.lastUsedFolder.id]);
          } else {
            toastr.error('Could not find the last used folder');
          }
        } catch (error) {
          console.error('Failed to use last folder:', error);
          toastr.error('Could not find the last used folder');
        }
      }
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep {
  .modal-content {
    width: max-content;
  }

  // Bootstrap sets max-height:none for centered + scrollable, so both classes are needed to win.
  // 100% of the dialog, which Bootstrap already caps per breakpoint — `100vh - 56px` would hardcode
  // the desktop margin and come out 40px short below 576px.
  .modal-dialog-centered.modal-dialog-scrollable .modal-content {
    max-height: 100%;
  }
}

/* Keep this modal above the CoreUI sidebar, which uses .c-sidebar { z-index: 1041 !important; }. */
[data-layout-folder-browser-modal] {
  z-index: 1042 !important;
}

.actions {
  display: flex;
  gap: 10px;

  .action-btn {
    padding: 5px 10px;
    cursor: pointer;
    position: relative;
    transition: color 0.3s ease;

    &.active {
      color: #209c86;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 4px;
      background-color: #209c86;
      transition: width 0.3s ease;
    }

    &:hover::after, &.active::after {
      width: 100%;
    }

    &:hover {
      color: #209c86;
    }
  }
}

.use-last-folder-btn,
.clear-search-btn {
  font-size: 0.8rem;
  color: #727272;
  text-decoration: none;

  &:hover {
    color: #209c86;
    text-decoration: none;
  }
}

.clear-search-btn {
  color: #dc3545;

  &:hover {
    color: #c82333;
  }
}
</style>
